import React, { useState, useRef, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router";
import { Button, PasswordInput, Messages } from "../components/ui";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import LandingHeader from "../components/layout/LandingHeader";
import LandingFooter from "../components/layout/LandingFooter";
import AuthService from "../services/AuthService";

function RedefinirSenhaConfirmacao() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const msg = useRef(null);

    const uidb64 = searchParams.get("uidb64");
    const token = searchParams.get("token");

    const [novaSenha, setNovaSenha] = useState("");
    const [confirmaNovaSenha, setConfirmaNovaSenha] = useState("");
    const [loading, setLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const authService = new AuthService();

    const showMessage = (severity, summary, detail) => {
        if (!msg.current) return;
        msg.current.clear();
        msg.current.show([{ severity, summary, detail }]);
    };

    useEffect(() => {
        if (!uidb64 || !token) {
            showMessage(
                "error",
                "Link Inválido",
                "Link de redefinição de senha inválido ou incompleto. Certifique-se de ter clicado no link enviado por e-mail."
            );
        }
    }, [uidb64, token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;

        if (!uidb64 || !token) {
            showMessage("error", "Erro!", "Link de redefinição inválido ou incompleto. Solicite um novo link.");
            return;
        }

        if (novaSenha !== confirmaNovaSenha) {
            showMessage("error", "Erro!", "As senhas não coincidem.");
            return;
        }

        if (novaSenha.length < 8) {
            showMessage("error", "Erro!", "A senha deve ter no mínimo 8 caracteres.");
            return;
        }

        setLoading(true);
        showMessage("info", "Aguarde", "Redefinindo sua senha...");

        try {
            const payload = JSON.stringify({
                uidb64: uidb64,
                token: token,
                nova_senha: novaSenha,
                confirma_nova_senha: confirmaNovaSenha
            });

            const response = await authService.confirmPasswordReset(payload);
            const data = await response.json();

            if (response.ok) {
                setIsSuccess(true);
                showMessage("success", "Sucesso!", data.detalhe || "Senha redefinida com sucesso!");
                setTimeout(() => {
                    navigate("/login");
                }, 3000);
            } else {
                const errorMsg =
                    data?.error ||
                    data?.erro ||
                    data?.nova_senha?.[0] ||
                    data?.token?.[0] ||
                    data?.detalhe ||
                    "Token inválido, expirado ou erro ao alterar senha. Solicite um novo link.";
                showMessage("error", "Erro!", errorMsg);
            }
        } catch (error) {
            showMessage("error", "Erro!", "Não foi possível conectar ao servidor. Tente novamente mais tarde.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="landing-page-wrapper min-h-screen flex flex-col justify-between bg-urb-dark-login">
            <LandingHeader />

            <main className="flex-1 flex flex-col items-center justify-center py-10 px-4">
                {/* Header Title */}
                <div className="text-center mb-6">
                    <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase m-0 flex items-center justify-center gap-1.5">
                        <span className="text-white">URB</span>
                        <span className="text-urb-primary">RACE</span>
                    </h1>
                    <p className="text-sm md:text-base mt-2 m-0 text-urb-text-secondary">
                        Cadastrar Nova Senha
                    </p>
                </div>

                {/* Card Container */}
                <div className="w-full max-w-md rounded-2xl border border-urb-border p-6 sm:p-8 shadow-2xl bg-urb-card/85 backdrop-blur-md">
                    <Messages ref={msg} />

                    {!isSuccess && uidb64 && token && (
                        <form onSubmit={handleSubmit} className="flex flex-col w-full mt-3">
                            {/* Nova Senha Input */}
                            <div className="flex flex-col gap-2 mb-4 w-full text-left">
                                <label htmlFor="novaSenha" className="text-sm font-semibold text-white">
                                    Nova Senha
                                </label>
                                <PasswordInput
                                    id="novaSenha"
                                    placeholder="No mínimo 8 caracteres"
                                    value={novaSenha}
                                    onChange={(e) => setNovaSenha(e.target.value)}
                                    disabled={loading}
                                    required
                                />
                            </div>

                            {/* Confirmar Nova Senha Input */}
                            <div className="flex flex-col gap-2 mb-5 w-full text-left">
                                <label htmlFor="confirmaNovaSenha" className="text-sm font-semibold text-white">
                                    Confirmar Nova Senha
                                </label>
                                <PasswordInput
                                    id="confirmaNovaSenha"
                                    placeholder="Digite a nova senha novamente"
                                    value={confirmaNovaSenha}
                                    onChange={(e) => setConfirmaNovaSenha(e.target.value)}
                                    disabled={loading}
                                    required
                                />
                            </div>

                            {/* Submit Button */}
                            <Button
                                type="submit"
                                label={loading ? "SALVANDO..." : "REDEFINIR SENHA"}
                                variant="primary"
                                size="lg"
                                className="w-full uppercase mb-4 shadow-lg"
                                loading={loading}
                                disabled={loading}
                            />

                            {/* Voltar para o Login */}
                            <div className="text-center mt-2">
                                <Link
                                    to="/login"
                                    className="inline-flex items-center gap-1.5 text-xs text-urb-text-secondary hover:text-white hover:underline transition-colors"
                                >
                                    <ArrowLeft className="w-3.5 h-3.5" />
                                    Voltar para o Login
                                </Link>
                            </div>
                        </form>
                    )}

                    {isSuccess && (
                        <div className="flex flex-col items-center text-center py-4">
                            <CheckCircle2 className="w-14 h-14 text-urb-primary mb-3" />
                            <h3 className="text-xl font-bold text-white mb-2">Senha Alterada!</h3>
                            <p className="text-sm text-urb-text-secondary mb-6 leading-relaxed">
                                Sua senha foi redefinida com sucesso. Redirecionando para a página de login em instantes...
                            </p>
                            <Button
                                label="IR PARA LOGIN AGORA"
                                onClick={() => navigate("/login")}
                                variant="primary"
                                size="lg"
                                className="w-full uppercase"
                            />
                        </div>
                    )}

                    {(!uidb64 || !token) && !isSuccess && (
                        <div className="flex flex-col items-center text-center py-4">
                            <Link to="/solicitar-recuperacao" className="w-full mt-3">
                                <Button
                                    label="SOLICITAR NOVO LINK"
                                    variant="primary"
                                    size="lg"
                                    className="w-full uppercase"
                                />
                            </Link>
                        </div>
                    )}
                </div>
            </main>

            <LandingFooter />
        </div>
    );
}

export default RedefinirSenhaConfirmacao;
